import { Fragment, useCallback } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H2 } from '@amzn/stencil-react-components/text';

import { RichTextEditorField } from '../../../shared/ui/RichTextEditorField';
import { AttachmentsInput } from '../../../shared/ui/AttachmentsInput';
import { MEDIA_UPLOAD_ACCEPT } from '../../../shared/data';
import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';
import { InfoTooltip } from '../../../shared/ui/forms/InfoTooltip';
import {
  TaskDetailsFields,
  type TaskDetailsValue,
} from '../../../shared/ui/forms/TaskDetailsFields';
import { Divider } from '../../../shared/ui/Divider';
import { VerticalDivider } from '../../../shared/ui/VerticalDivider';
import { useMessages } from '../../../shared/i18n/useMessages';
import type { ReviewQuestion, QuestionContent, TaskDetails } from '../logic/reviewTypes';

const BUNDLE = 'features.audit-whiz.translations.reviewQuestions';

type CriteriaKey =
  | 'criteriaStepHeading'
  | 'criteriaInfoAltText'
  | 'criteriaQuestionTextLabel'
  | 'criteriaQuestionTextPlaceholder'
  | 'criteriaQuestionTextInfo'
  | 'criteriaAuditInstructionLabel'
  | 'criteriaAuditInstructionPlaceholder'
  | 'criteriaAuditInstructionInfo'
  | 'criteriaPassLabel'
  | 'criteriaPassPlaceholder'
  | 'criteriaPassInfo'
  | 'criteriaFailLabel'
  | 'criteriaFailPlaceholder'
  | 'criteriaFailInfo'
  | 'criteriaJustificationLabel'
  | 'criteriaJustificationPlaceholder'
  | 'criteriaJustificationInfo'
  | 'criteriaAttachDragDropLabel'
  | 'criteriaTaskHeading'
  | 'criteriaTaskNameLabel'
  | 'criteriaTaskNamePlaceholder'
  | 'criteriaTaskDescriptionLabel'
  | 'criteriaTaskDescriptionPlaceholder'
  | 'criteriaTaskDurationLabel'
  | 'criteriaTaskDurationPlaceholder';

/** The editable rich-text fields on `questionDetails`. */
export type CriteriaField =
  | 'questionText'
  | 'auditInstruction'
  | 'passCriteria'
  | 'failCriteria'
  | 'justification';

/** Per-field attachment files for one question's criteria fields. */
export type CriteriaAttachments = Record<CriteriaField, File[]>;

/** Reference-stable empty per-field attachments map. Exported so the owning
 *  page can seed state without re-allocating (and so identity checks hold). */
export const EMPTY_CRITERIA_ATTACHMENTS: CriteriaAttachments = {
  questionText: [],
  auditInstruction: [],
  passCriteria: [],
  failCriteria: [],
  justification: [],
};

export interface AuditCriteriaStepProps {
  /** The question under review whose criteria this step edits. */
  readonly question: ReviewQuestion;
  /** Emits a new question with the edited content. Parent owns draft state. */
  readonly onQuestionChange: (next: ReviewQuestion) => void;
  /**
   * Files attached per criteria field. Controlled by the owning page so they
   * survive step navigation (this component unmounts when the user moves to
   * another wizard step). PHASE 1: button-only — held in the page's UI state,
   * not uploaded or persisted onto the draft yet.
   */
  readonly attachments: CriteriaAttachments;
  /** Emits the full updated file list for one field on attach/remove. */
  readonly onAttachmentsChange: (field: CriteriaField, files: File[]) => void;
  /** Disables every field (e.g. once the question is published). */
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/**
 * First review wizard step: the editable "Audit criteria" form. Each criteria
 * field (question text, audit instructions, pass/fail criteria, justification)
 * and the optional task description are rich-text editors, reusing the shared
 * `RichTextEditorField` (Stencil/TipTap) so the review flow matches the manual
 * question-creation flow without a second editor implementation. Task name and
 * duration are plain inputs.
 *
 * Fully controlled — it derives a new `ReviewQuestion` on every edit and hands
 * it to `onQuestionChange`; the owning page/hook holds the draft state.
 */
export function AuditCriteriaStep({
  question,
  onQuestionChange,
  attachments,
  onAttachmentsChange,
  disabled = false,
  dataTestId,
}: AuditCriteriaStepProps): JSX.Element {
  const { t, isLoading: isBundleLoading } = useMessages<CriteriaKey>(BUNDLE);
  const { questionDetails, taskDetails } = question.content;

  // Patch a single questionDetails field, preserving the rest of the draft.
  const handleCriteriaChange = useCallback(
    (field: CriteriaField, value: string) => {
      onQuestionChange({
        ...question,
        content: {
          ...question.content,
          questionDetails: { ...question.content.questionDetails, [field]: value },
        },
      });
    },
    [question, onQuestionChange],
  );

  // Write the edited task back onto the draft's content. TaskDetailsFields
  // owns the per-field editing; this just re-parents the value into the
  // question and emits the new draft.
  const handleTaskChange = useCallback(
    (next: TaskDetailsValue) => {
      // Normalize to the Helios TaskDetails shape (omit the duration key when
      // absent) so it satisfies exactOptionalPropertyTypes.
      const nextTask: TaskDetails =
        next.taskDurationDays === undefined
          ? { taskName: next.taskName, taskDescription: next.taskDescription }
          : {
              taskName: next.taskName,
              taskDescription: next.taskDescription,
              taskDurationDays: next.taskDurationDays,
            };
      const nextContent: QuestionContent = { ...question.content, taskDetails: nextTask };
      onQuestionChange({ ...question, content: nextContent });
    },
    [question, onQuestionChange],
  );

  const criteriaFields: readonly {
    key: CriteriaField;
    label: string;
    placeholder: string;
    info: string;
    value: string;
  }[] = [
    {
      key: 'questionText',
      label: t('criteriaQuestionTextLabel'),
      placeholder: t('criteriaQuestionTextPlaceholder'),
      info: t('criteriaQuestionTextInfo'),
      value: questionDetails.questionText,
    },
    {
      key: 'auditInstruction',
      label: t('criteriaAuditInstructionLabel'),
      placeholder: t('criteriaAuditInstructionPlaceholder'),
      info: t('criteriaAuditInstructionInfo'),
      value: questionDetails.auditInstruction,
    },
    {
      key: 'passCriteria',
      label: t('criteriaPassLabel'),
      placeholder: t('criteriaPassPlaceholder'),
      info: t('criteriaPassInfo'),
      value: questionDetails.passCriteria,
    },
    {
      key: 'failCriteria',
      label: t('criteriaFailLabel'),
      placeholder: t('criteriaFailPlaceholder'),
      info: t('criteriaFailInfo'),
      value: questionDetails.failCriteria,
    },
    {
      key: 'justification',
      label: t('criteriaJustificationLabel'),
      placeholder: t('criteriaJustificationPlaceholder'),
      info: t('criteriaJustificationInfo'),
      value: questionDetails.justification,
    },
  ];

  const infoAltText = t('criteriaInfoAltText');

  // The step's task is always shown (optional fields, no add/remove here),
  // so hand TaskDetailsFields an empty shell when the draft has no task yet.
  const taskValue: TaskDetailsValue = taskDetails ?? EMPTY_TASK_DETAILS;

  return (
    // Flat content (no section cards): the review page wraps the whole step in
    // a single outer card, so Audit criteria and Task details render as one
    // continuous flow separated by a Divider — matching the mock. Section
    // headings are H2 (the page title is the H1).
    <Col
      gridGap="dimensions.spacing.400"
      width="100%"
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      <Col gridGap="dimensions.spacing.400" width="100%">
        <H2>{t('criteriaStepHeading')}</H2>

        {/* The editor's toolbar config needs resolved i18n labels, so hold
         *  rendering until the bundle loads (mirrors TaskFormFields). */}
        {isBundleLoading
          ? null
          : criteriaFields.map((field, index) => (
              <Fragment key={field.key}>
                {index > 0 && <Divider />}
                <CriteriaEditorField
                  field={field.key}
                  label={field.label}
                  placeholder={field.placeholder}
                  info={field.info}
                  infoAltText={infoAltText}
                  value={field.value}
                  disabled={disabled}
                  onChange={handleCriteriaChange}
                  files={attachments[field.key]}
                  onFilesChange={onAttachmentsChange}
                  dragAndDropText={t('criteriaAttachDragDropLabel')}
                  {...(dataTestId !== undefined
                    ? { dataTestId: `${dataTestId}-${field.key}` }
                    : {})}
                />
              </Fragment>
            ))}
      </Col>

      {/* Divider separates the criteria fields from the optional Task details
       *  section — replaces the old peer-card boundary. */}
      <Divider />

      <Col
        gridGap="dimensions.spacing.300"
        width="100%"
        {...(dataTestId !== undefined
          ? { dataTestId: `${dataTestId}-task-section` }
          : {})}
      >
        <H2>{t('criteriaTaskHeading')}</H2>

        {/* TaskDetailsFields renders a RichTextEditorField whose toolbar
         *  needs resolved i18n labels, so hold until the bundle loads. */}
        {isBundleLoading ? null : (
          <TaskDetailsFields
            value={taskValue}
            onChange={handleTaskChange}
            disabled={disabled}
            isRequired={false}
            labels={{
              nameLabel: t('criteriaTaskNameLabel'),
              namePlaceholder: t('criteriaTaskNamePlaceholder'),
              descriptionLabel: t('criteriaTaskDescriptionLabel'),
              descriptionPlaceholder: t('criteriaTaskDescriptionPlaceholder'),
              durationLabel: t('criteriaTaskDurationLabel'),
              durationPlaceholder: t('criteriaTaskDurationPlaceholder'),
            }}
            {...(dataTestId !== undefined ? { dataTestId } : {})}
          />
        )}
      </Col>
    </Col>
  );
}

/** An empty task shell used when the draft has no task yet, so the first task
 *  edit produces a well-formed TaskDetails. Module-level for reference
 *  stability. */
const EMPTY_TASK_DETAILS: TaskDetails = { taskName: '', taskDescription: '' };

/** One labelled rich-text criteria field. Extracted so the editor list maps to
 *  a single component (keeps the JSX DRY) and its onChange stays typed. */
function CriteriaEditorField({
  field,
  label,
  placeholder,
  info,
  infoAltText,
  value,
  disabled,
  onChange,
  files,
  onFilesChange,
  dragAndDropText,
  dataTestId,
}: {
  readonly field: CriteriaField;
  readonly label: string;
  readonly placeholder: string;
  /** Info-tooltip help text shown next to the label. */
  readonly info: string;
  /** Accessible label for the info-icon trigger. */
  readonly infoAltText: string;
  readonly value: string;
  readonly disabled: boolean;
  readonly onChange: (field: CriteriaField, value: string) => void;
  /** Files attached to this field via the attachment picker. */
  readonly files: readonly File[];
  /** Emits the full updated file list for this field on attach/remove. */
  readonly onFilesChange: (field: CriteriaField, files: File[]) => void;
  readonly dragAndDropText: string;
  readonly dataTestId?: string;
}): JSX.Element {
  const handleChange = useCallback(
    (html: string) => onChange(field, html),
    [field, onChange],
  );
  const handleFilesChange = useCallback(
    (next: File[]) => onFilesChange(field, next),
    [field, onFilesChange],
  );
  return (
    <Col gridGap="dimensions.spacing.100">
      {/* Every criteria field is required (WHIZ-002): label + red asterisk,
       *  then an info tooltip carrying the manual-flow help copy. */}
      <Row alignItems="center" gridGap="dimensions.spacing.200">
        <RequiredLabel isRequired hasError={false}>
          {label}
        </RequiredLabel>
        <VerticalDivider />
        <InfoTooltip
          text={info}
          ariaLabel={infoAltText}
          {...(dataTestId !== undefined ? { dataTestId: `${dataTestId}-info` } : {})}
        />
      </Row>
      <RichTextEditorField
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        ariaLabel={label}
        {...(dataTestId !== undefined ? { dataTestId } : {})}
      />
      {/* Field-level attachment control (attach files to the question — separate
       *  from the RTE toolbar's inline-image insert). Reuses the shared
       *  AttachmentsInput; upload wiring is deferred (files held in the step's
       *  local state for now). */}
      <AttachmentsInput
        files={[...files]}
        onFilesChange={handleFilesChange}
        dragAndDropText={dragAndDropText}
        accept={MEDIA_UPLOAD_ACCEPT}
        disabled={disabled}
        showCapture={false}
      />
    </Col>
  );
}
